/**
 * TAB 4 — Treasury Controls (requirement §31 to §36, AC-16, AC-17, AC-19).
 *
 * Freeze and unfreeze only. There is deliberately NO Add VC / Redeem VC /
 * Transfer VC here (§31.1, AC-03), and no balance setup: the treasury starts at
 * 0 and is credited by the inactive-agent migration, so there is nothing to
 * configure. Daily report recipients come from RTP_REPORT_EMAILS on the server.
 */

import * as React from "react";
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Chip,
} from "@mui/material";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import {
  StatusChip,
  Panel,
  SectionTitle,
  ErrorBar,
  Loading,
  fmtVC,
  fmtDateTime,
  dialogSx,
  primaryBtnSx,
  secondaryBtnSx,
  GOLD,
} from "../components/common";
import { getControls, freezeTreasury, unfreezeTreasury } from "../api";

export const ControlsTab = ({ onStatusChange }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await getControls();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) return <Loading height={300} />;

  const t = data?.treasury || {};

  return (
    <Box>
      <ErrorBar error={error} onClose={() => setError("")} />
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      <FreezePanel
        treasury={t}
        freezeScope={data?.freezeScope}
        automaticFreeze={data?.automaticFreeze}
        onDone={async (message) => {
          setNotice(message);
          await load();
          if (onStatusChange) onStatusChange();
        }}
        onError={setError}
      />
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Freeze / unfreeze (§32 to §36)
// ═════════════════════════════════════════════════════════════════════════════

const FreezePanel = ({ treasury, freezeScope, automaticFreeze, onDone, onError }) => {
  const [dialog, setDialog] = React.useState(null); // 'freeze' | 'unfreeze'
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const isFrozen = treasury.status === "FROZEN";

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const result =
        dialog === "freeze" ? await freezeTreasury(reason) : await unfreezeTreasury(reason);
      setDialog(null);
      setReason("");
      onDone(result.message);
    } catch (err) {
      onError(err.message);
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SectionTitle
        action={
          <Chip
            size="small"
            label={`Main balance: ${fmtVC(treasury.currentBalance)} VC`}
            sx={{ backgroundColor: "#242426", color: GOLD, fontSize: "0.7rem" }}
          />
        }
      >
        Treasury Status
      </SectionTitle>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Panel sx={{ height: "100%" }}>
            <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem", mb: 1 }}>
              CURRENT STATUS
            </Typography>
            <StatusChip value={treasury.status} />
            <Box sx={{ mt: 2 }}>
              {isFrozen ? (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<LockOpenIcon />}
                  onClick={() => setDialog("unfreeze")}
                  sx={{ backgroundColor: "#2E7D32", textTransform: "none" }}
                >
                  Unfreeze Treasury
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AcUnitIcon />}
                  onClick={() => setDialog("freeze")}
                  sx={{ backgroundColor: "#C62828", textTransform: "none" }}
                >
                  Freeze Treasury
                </Button>
              )}
            </Box>
          </Panel>
        </Grid>

        <Grid item xs={12} md={9}>
          <Panel sx={{ height: "100%" }}>
            <Grid container spacing={2}>
              <Field label="Freeze Time" value={fmtDateTime(treasury.freezeTime)} />
              <Field label="Freeze Reason" value={treasury.freezeReason || "-"} />
              <Field label="Freeze Trigger" value={treasury.freezeTrigger || "-"} />
              <Field label="Frozen By" value={treasury.frozenByName || "-"} />
              <Field label="Last Unfreeze Time" value={fmtDateTime(treasury.lastUnfreezeTime)} />
              <Field label="Last Unfreeze Reason" value={treasury.lastUnfreezeReason || "-"} />
            </Grid>
          </Panel>
        </Grid>
      </Grid>

      {freezeScope && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>What a freeze does:</strong> {freezeScope.description}
          <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {freezeScope.blocked.map((t) => (
              <Chip key={t} size="small" label={`Blocked: ${t}`} sx={{ backgroundColor: "#4E1C1C", color: "#EF9A9A", fontSize: "0.6rem" }} />
            ))}
            {freezeScope.allowed.map((t) => (
              <Chip key={t} size="small" label={`Allowed: ${t}`} sx={{ backgroundColor: "#1C4E22", color: "#A5D6A7", fontSize: "0.6rem" }} />
            ))}
          </Box>
        </Alert>
      )}

      {automaticFreeze && !automaticFreeze.enabled && (
        <Typography sx={{ color: "#666", fontSize: "0.7rem" }}>{automaticFreeze.note}</Typography>
      )}

      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} maxWidth="sm" fullWidth sx={dialogSx}>
        <DialogTitle>
          {dialog === "freeze" ? "Freeze House Treasury?" : "Unfreeze House Treasury?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialog === "freeze"
              ? "This blocks every recharge and redeem across the whole hierarchy immediately, enforced in the backend. Gameplay and bonuses continue running."
              : "This resumes recharge and redeem across the platform."}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            required
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Recorded permanently in the audit log."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDialog(null)} disabled={busy} sx={secondaryBtnSx}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !reason.trim()}
            variant="contained"
            sx={primaryBtnSx}
          >
            {busy ? "Working..." : dialog === "freeze" ? "Freeze" : "Unfreeze"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography sx={{ color: "#9E9E9E", fontSize: "0.68rem" }}>{label}</Typography>
    <Typography sx={{ color: "#FFF", fontSize: "0.85rem", mt: 0.3 }}>{value}</Typography>
  </Grid>
);

export default ControlsTab;
